<script setup lang="ts">
  import { ArrowLeft } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'

  const { t } = useTranslation({
    en: {
      backRequiredActions: 'Back to required actions',
      goBack: 'Go back'
    },
    de: {
      backRequiredActions: 'Zurück zu erforderlichen Aktionen',
      goBack: 'Zurück'
    }
  })

  const router = useRouter()

  interface Props {
    title?: string
    showBackButton?: boolean
    backButtonDataCy?: string
    titleDataCy?: string
    sidebarVisible?: boolean
    shouldShowSidebar?: boolean
    isExternalRoute?: boolean
    fullWidth?: boolean
  }

  withDefaults(defineProps<Props>(), {
    title: 'Questionnaire',
    showBackButton: true,
    backButtonDataCy: 'MobileBackButton',
    titleDataCy: 'MobileTitle',
    sidebarVisible: true,
    shouldShowSidebar: true,
    isExternalRoute: false,
    fullWidth: false
  })

  const handleBackClick = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const sessionToken = urlParams.get('session_token')
    const accessToken = urlParams.get('access_token')

    const query: Record<string, string> = {}
    if (sessionToken) {
      query.session_token = sessionToken
    } else if (accessToken) {
      query.access_token = accessToken
    }

    router.push({
      path: ROUTES.CLIENT_PORTAL.HOME,
      query
    })
  }
</script>

<template>
  <!-- Mobile Header -->
  <header class="flex flex-col gap-3 p-4 md:hidden">
    <div class="flex items-center gap-6">
      <button
        v-if="showBackButton"
        :data-cy="backButtonDataCy"
        :aria-label="t('goBack')"
        class="flex items-center gap-2.5 pt-0.5"
        @click="handleBackClick"
      >
        <ArrowLeft
          class="h-6 w-6 text-gray-800"
          aria-hidden="true"
        />
      </button>
      <div class="flex-1">
        <h1
          class="text-md font-semibold leading-7 text-gray-800"
          :data-cy="titleDataCy"
          tabindex="-1"
        >
          {{ title }}
        </h1>
      </div>
    </div>
  </header>

  <!-- Desktop Header -->
  <header
    class="fixed left-0 right-0 top-0 z-10 hidden flex-col gap-2 bg-gray-50 px-4 py-4 transition-all
      duration-300 ease-in-out md:flex"
    :class="
      fullWidth
        ? 'left-0 shadow-lg'
        : shouldShowSidebar
          ? sidebarVisible
            ? 'md:left-[250px]'
            : 'md:left-16'
          : 'left-0'
    "
  >
    <nav
      v-if="showBackButton && !isExternalRoute"
      aria-label="Breadcrumb"
      class="flex justify-start"
    >
      <Button
        variant="ghost"
        size="sm"
        class="flex h-auto items-center gap-1 p-0 text-xs font-medium text-gray-500 underline hover:bg-transparent
          hover:text-gray-900"
        @click="handleBackClick"
      >
        <ArrowLeft
          class="h-4 w-4"
          aria-hidden="true"
        />
        <span>{{ t('backRequiredActions') }}</span>
      </Button>
    </nav>
    <h1
      v-if="title"
      class="text-lg font-semibold text-gray-900"
      tabindex="-1"
    >
      {{ title }}
    </h1>
  </header>
</template>
